/**
 * Système de notifications toast pour les messages système
 * 
 * Gère plusieurs types de toasts :
 * - Info : messages système génériques
 * - Top/Bulle : résultats de donne avec avatars
 * - Résultats du tournoi : classement final avec podium
 * - Votre résultat : pourcentage avec barre de progression
 * - Arrivée/départ de joueurs : avec avatar et icône directionnelle
 */

import { useEffect, useCallback } from 'react';
import { PlayerAvatar } from './PlayerAvatar';

// ============================================
// Types
// ============================================

export interface Toast {
  id: string;
  text: string;
  type: 'info' | 'result';
  createdAt: number;
}

interface SystemToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

// ============================================
// Constantes
// ============================================

const TOAST_DURATION = 7000;
const TOAST_FADE_DURATION = 600;
const TOAST_JOIN_LEAVE_DURATION = 3000;  // Plus court pour les join/leave
const MAX_VISIBLE_TOASTS = 3;

// ============================================
// Parsers pour les différents types de toasts
// ============================================

// --- Top / Bulle ---

interface ParsedTopBulleSection {
  kind: 'top' | 'bulle';
  pseudos: string[];
}

function parseTopBulle(text: string): ParsedTopBulleSection[] | null {
  const lines = text.split('\n');
  const sections: ParsedTopBulleSection[] = [];
  for (const line of lines) {
    const match = line.match(/^(Top|Bulle)\s*:\s*(.+)$/i);
    if (match) {
      const kind = match[1].toLowerCase() === 'top' ? 'top' : 'bulle';
      const pseudos = match[2].split(',').map(s => s.trim()).filter(Boolean);
      sections.push({ kind, pseudos });
    }
  }
  return sections.length > 0 ? sections : null;
}

// --- Résultats du tournoi ---

interface ParsedTournamentResult {
  title: string;
  rankings: { rank: number; pseudo: string }[];
  myResult?: { percentage: number; rank: number; total: number };
}

function parseTournamentResults(text: string): ParsedTournamentResult | null {
  if (!text.startsWith('__RESULTS__')) return null;
  const raw = text.replace('__RESULTS__', '');
  const lines = raw.split('\n');
  
  const result: ParsedTournamentResult = {
    title: '',
    rankings: [],
  };
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    // Titre (regex souple pour gérer les variantes d'encodage de l'accent)
    if (/^R[ée]sultats du tournoi/i.test(trimmed)) {
      result.title = trimmed;
      continue;
    }
    
    // Ligne de classement : "1- Bot1" ou "2- TOUPIE32"
    const rankMatch = trimmed.match(/^(\d+)\s*[-–]\s*(.+)$/);
    if (rankMatch) {
      result.rankings.push({ rank: parseInt(rankMatch[1], 10), pseudo: rankMatch[2].trim() });
      continue;
    }
    
    // "Votre résultat" (marqueur __MYRESULT__)
    if (trimmed.startsWith('__MYRESULT__')) {
      const myText = trimmed.replace('__MYRESULT__', '');
      const pctMatch = myText.match(/(\d+)\s*%/);
      const clMatch = myText.match(/classement\s*=\s*(\d+)\s*sur\s*(\d+)/i);
      if (pctMatch) {
        result.myResult = {
          percentage: parseInt(pctMatch[1], 10),
          rank: clMatch ? parseInt(clMatch[1], 10) : 0,
          total: clMatch ? parseInt(clMatch[2], 10) : 0,
        };
      }
    }
  }
  
  return result.title ? result : null;
}

// --- "Votre résultat" seul ---

interface ParsedMyResult {
  percentage: number;
  rank: number;
  total: number;
}

function parseMyResult(text: string): ParsedMyResult | null {
  if (!text.startsWith('__MYRESULT__')) return null;
  const raw = text.replace('__MYRESULT__', '');
  const pctMatch = raw.match(/(\d+)\s*%/);
  const clMatch = raw.match(/classement\s*=\s*(\d+)\s*sur\s*(\d+)/i);
  if (!pctMatch) return null;
  return {
    percentage: parseInt(pctMatch[1], 10),
    rank: clMatch ? parseInt(clMatch[1], 10) : 0,
    total: clMatch ? parseInt(clMatch[2], 10) : 0,
  };
}

// --- Join / Leave ---

interface ParsedJoinLeave {
  kind: 'join' | 'leave';
  pseudo: string;
}

function parseJoinLeave(text: string): ParsedJoinLeave | null {
  if (text.startsWith('__JOIN__')) return { kind: 'join', pseudo: text.replace('__JOIN__', '') };
  if (text.startsWith('__LEAVE__')) return { kind: 'leave', pseudo: text.replace('__LEAVE__', '') };
  return null;
}

// ============================================
// Sous-composants de rendu
// ============================================

const SECTION_CONFIG = {
  top: { emoji: '\u{1F3C6}', label: 'Top' },
  bulle: { emoji: '\u{1F4A5}', label: 'Bulle' },
} as const;

function TopBulleSection({ section }: { section: ParsedTopBulleSection }) {
  const { emoji, label } = SECTION_CONFIG[section.kind];
  return (
    <div className={`toast-topbulle-section toast-${section.kind}`}>
      <span className="toast-topbulle-header">
        <span className="toast-topbulle-emoji">{emoji}</span>
        <span className="toast-topbulle-label">{label}</span>
      </span>
      <div className="toast-topbulle-players">
        {section.pseudos.map((pseudo, i) => (
          <span key={`${pseudo}-${i}`} className="toast-topbulle-player">
            <PlayerAvatar pseudo={pseudo} size={18} />
            <span className="toast-topbulle-pseudo">{pseudo}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TopBulleToast({ sections }: { sections: ParsedTopBulleSection[] }) {
  return (
    <div className="toast-topbulle">
      {sections.map((section, i) => (
        <TopBulleSection key={`${section.kind}-${i}`} section={section} />
      ))}
    </div>
  );
}

const RANK_EMOJI = ['\u{1F947}', '\u{1F948}', '\u{1F949}']; // 🥇🥈🥉

function TournamentResultToast({ data }: { data: ParsedTournamentResult }) {
  return (
    <div className="toast-tournament-results">
      <div className="toast-tournament-title">{'\u{1F3C6}'} {data.title}</div>
      <div className="toast-tournament-rankings">
        {data.rankings.map(({ rank, pseudo }) => (
          <div key={`${rank}-${pseudo}`} className="toast-tournament-rank-row">
            <span className="toast-rank-badge">
              {rank <= 3 ? RANK_EMOJI[rank - 1] : `${rank}.`}
            </span>
            <PlayerAvatar pseudo={pseudo} size={18} />
            <span className="toast-rank-pseudo">{pseudo}</span>
          </div>
        ))}
      </div>
      {data.myResult && <MyResultDisplay data={data.myResult} />}
    </div>
  );
}

function MyResultDisplay({ data }: { data: ParsedMyResult }) {
  return (
    <div className="toast-my-result">
      <div className="toast-my-result-header">
        Votre résultat
      </div>
      <div className="toast-my-result-row">
        <span className="toast-my-result-pct">{data.percentage}%</span>
        {data.rank > 0 && data.total > 0 && (
          <span className="toast-my-result-rank">{data.rank}<small>/{data.total}</small></span>
        )}
      </div>
      <div className="toast-my-result-bar">
        <div className="toast-my-result-fill" style={{ width: `${data.percentage}%` }} />
      </div>
    </div>
  );
}

function JoinLeaveToast({ data }: { data: ParsedJoinLeave }) {
  const isJoin = data.kind === 'join';
  return (
    <div className={`toast-joinleave toast-${data.kind}`}>
      <span className="toast-joinleave-icon">{isJoin ? '\u{2795}' : '\u{2796}'}</span>
      <PlayerAvatar pseudo={data.pseudo} size={18} />
      <span className="toast-joinleave-pseudo">{data.pseudo}</span>
    </div>
  );
}

// ============================================
// Composant principal
// ============================================

function getToastDuration(toast: Toast): number {
  if (toast.text.startsWith('__JOIN__') || toast.text.startsWith('__LEAVE__')) {
    return TOAST_JOIN_LEAVE_DURATION;
  }
  if (toast.text.startsWith('__RESULTS__')) {
    return 12000; // Plus long pour les résultats du tournoi
  }
  return TOAST_DURATION;
}

export default function SystemToastContainer({ toasts, onDismiss }: SystemToastContainerProps) {
  // Auto-dismiss
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map(toast => {
      const duration = getToastDuration(toast);
      const elapsed = Date.now() - toast.createdAt;
      const remaining = Math.max(0, duration - elapsed);
      return setTimeout(() => onDismiss(toast.id), remaining);
    });
    return () => timers.forEach(clearTimeout);
  }, [toasts, onDismiss]);

  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS);
  if (visibleToasts.length === 0) return null;

  return (
    <div className="system-toast-container">
      {visibleToasts.map(toast => {
        const duration = getToastDuration(toast);
        const age = Date.now() - toast.createdAt;
        const isFading = age > duration - TOAST_FADE_DURATION;

        // Déterminer le type de rendu
        const joinLeave = parseJoinLeave(toast.text);
        const topBulle = !joinLeave ? parseTopBulle(toast.text) : null;
        const tournamentResult = !joinLeave && !topBulle ? parseTournamentResults(toast.text) : null;
        const myResult = !joinLeave && !topBulle && !tournamentResult ? parseMyResult(toast.text) : null;

        const extraClass = joinLeave ? 'compact' : '';

        return (
          <div
            key={toast.id}
            className={`system-toast ${toast.type} ${isFading ? 'fading' : ''} ${extraClass}`}
            onClick={() => onDismiss(toast.id)}
          >
            {joinLeave ? (
              <JoinLeaveToast data={joinLeave} />
            ) : topBulle ? (
              <TopBulleToast sections={topBulle} />
            ) : tournamentResult ? (
              <TournamentResultToast data={tournamentResult} />
            ) : myResult ? (
              <MyResultDisplay data={myResult} />
            ) : (
              <span className="toast-text">{toast.text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Hook utilitaire
// ============================================

export function useToastQueue() {
  const addToast = useCallback((
    setToasts: React.Dispatch<React.SetStateAction<Toast[]>>,
    text: string,
    type: 'info' | 'result' = 'info'
  ) => {
    const newToast: Toast = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      text,
      type,
      createdAt: Date.now(),
    };
    setToasts(prev => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((
    setToasts: React.Dispatch<React.SetStateAction<Toast[]>>,
    id: string
  ) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { addToast, dismissToast };
}
